import { MarkerColor } from "@rio-cloud/rio-uikit/mapTypes";
import { Route } from "@types";
import { useState } from "react";
import {
  getBoundingBox,
  getRandomValueToForceRerender,
  ICoordinates,
  IRoutePoint,
  ISandboxMarker,
} from "src/components/Map";
import { decode } from "src/utils/flexiblePolyline";

interface Position {
  lat: number;
  lng: number;
}

interface BoundingBox {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

interface Config {
  padding: number;
}

const GetIconName = (mode: string) => {
  let out = "";

  switch (mode) {
    case "ferry":
      out = "rioglyph rioglyph-ship";
      break;
    case "street_truck":
      out = "rioglyph rioglyph-truck";
      break;
    default:
      out = "rioglyph rioglyph-pushpin";
      break;
  }

  return out;
};

const GetBackground = (mode: string) => {
  let out: MarkerColor = "bg-map-marker-route";

  switch (mode) {
    case "ferry":
      out = "bg-map-marker-asset";
      break;
    case "street_truck":
      out = "bg-map-marker-asset";
      break;

    default:
      out = "bg-map-marker-route";
      break;
  }

  return out;
};

export const useHandleRoutes = () => {
  const WORLD_DIM = { width: 256, height: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  function calculateZoomLevelFromBoundingBox(bounds: BoundingBox, mapDim: { width: number; height: number }) {
    const ne = { lat: bounds.top, lng: bounds.right };
    const sw = { lat: bounds.bottom, lng: bounds.left };

    const latFraction = (latRad(ne.lat) - latRad(sw.lat)) / Math.PI;
    const lngDiff = ne.lng - sw.lng;
    const lngFraction = (lngDiff < 0 ? lngDiff + 360 : lngDiff) / 360;

    const latZoom = zoom(mapDim.height, WORLD_DIM.height, latFraction);
    const lngZoom = zoom(mapDim.width, WORLD_DIM.width, lngFraction);

    return Math.min(latZoom, lngZoom, ZOOM_MAX);
  }

  const handleRoutes = (routes: any[], via: any[], config?: Config) => {
    let segments: IRoutePoint[] = [];
    let markers: ISandboxMarker[] = [];
    let position: Position | undefined = {
      lat: -23.5475,
      lng: -46.63611,
    };
    let boundingBox: BoundingBox | undefined = undefined;
    let zoom = 10;

    if (routes.length) {
      let ArrayOfShape: any[] = [];
      const route = routes?.[0];
      const data = route?.sections || [];

      const getPolylineArray = data.map((item: any) => {
        return {
          shape: decode(item.polyline),
        };
      });

      const pins = data.reduce((acum: ISandboxMarker[], curr: any, index: number, arr: any) => {
        if (index === 0) {
          const markerOrigin: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["start"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.departure.place.location.lat,
              lng: curr.departure.place.location.lng,
            },
          };
          acum.push(markerOrigin);
        }
        if (index === arr.length - 1) {
          const markerDestination: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: ["finish"],
              name: "",
              markerColor: "bg-map-marker-route",
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerDestination);
        } else {
          const markerStopover: ISandboxMarker = {
            id: getRandomValueToForceRerender().toString(),
            markerProps: {
              iconNames: [GetIconName(curr.transport.mode)],
              name: via?.[index]?.position?.label || "",
              markerColor: GetBackground(curr.transport.mode),
              active: false,
              fixed: false,
            },
            position: {
              lat: curr.arrival.place.location.lat,
              lng: curr.arrival.place.location.lng,
            },
          };
          acum.push(markerStopover);
        }

        return acum;
      }, []);

      getPolylineArray.forEach((item: any) => {
        ArrayOfShape.push(item.shape.polyline);
      });
      const concatArrays = ArrayOfShape.flat();
      const getPoints = concatArrays.map((value) => {
        return {
          lat: value[0].toString(),
          lng: value[1].toString(),
        };
      });

      segments = getPoints as IRoutePoint[];
      markers = pins;

      if (getPoints?.length > 0) {
        // Cálculo da latitude e longitude médias
        const latSum = segments.reduce((acc, p) => acc + Number(p.lat), 0);
        const lngSum = segments.reduce((acc, p) => acc + Number(p.lng), 0);
        position = {
          lat: latSum / segments.length,
          lng: lngSum / segments.length,
        };

        if (config?.padding) {
          const b = getBoundingBox(getPoints);

          boundingBox = {
            top: b.top + config.padding,
            bottom: b.bottom - config.padding,
            left: b.left + config.padding,
            right: b.right + config.padding,
          };
        } else {
          boundingBox = getBoundingBox(getPoints);
        }

        zoom = calculateZoomLevelFromBoundingBox(boundingBox, {
          width: 800, // Você pode passar isso como parâmetro em config
          height: 600,
        });
      }
    } else {
      segments = [];
      markers = [];
    }

    return {
      segments,
      markers,
      position,
      boundingBox,
      zoom,
    };
  };

  const handleGenerateMarker = ({
    name,
    mode,
    lat,
    lng,
  }: {
    name?: string;
    mode: string;
    lat: number;
    lng: number;
  }) => {
    const marker: ISandboxMarker = {
      id: getRandomValueToForceRerender().toString(),
      markerProps: {
        iconNames: [GetIconName(mode)],
        name: name || "",
        markerColor: GetBackground(mode),
        active: false,
        fixed: false,
      },
      position: {
        lat,
        lng,
      },
    };

    return marker;
  };

  const handleSimpleLocation = (v: number) => {
    if (!v) {
      return "0";
    }

    return v?.toString()?.slice(0, 12);
  };

  const handleIconName = (name?: string) => {
    const types: Record<string, string> = {
      car: "rioglyph rioglyph-car",
      bus: "rioglyph rioglyph-bus",
      van: "rioglyph rioglyph-van",
      truck: "rioglyph rioglyph-truck",
      trailer: "rioglyph rioglyph-trailer",
    };

    return types[name ?? ''] || 'rioglyph rioglyph-truck';
  };
  return {
    handleRoutes,
    handleGenerateMarker,
    handleSimpleLocation,
    handleIconName,
  };
};

